import { useQuery } from '@tanstack/react-query'
import { v1Client } from '../api'

import {
  useMessagesStore,
  useReadAllChatMessages,
  useActiveChatId,
  isTurnLive,
  waitForTurnEnd,
  getQueueForChat,
} from '../store'
import { mergeRefetchedMessages } from './merge-refetched-messages'
import { messagesFreshnessOptions, messagesQueryKey } from './messages-query'
import { useAuth } from '../providers'
import { extractThinkingContent } from '../utils/chat'
import { ChatMessage, ToolCall } from '../types'
import { reconstructContentParts } from '../components/chat/message/tools/build-render-parts'
import { useSubmitMessage } from './useSubmitMessage'
import { useStopMessage } from './useCancelMessage'
import { useAddChatToast } from '../store/toasts'
import { useCallback } from 'react'

/**
 * Normalize the raw messages array returned by GET /conversations/:id/messages
 * into the ChatMessage shape the atom + renderers expect. Pure — no hooks, no
 * I/O — so it can be reused outside the useLoadMessages queryFn (e.g. by a
 * prefetch path that wants to populate the atom before opening a chat drawer).
 */
export function normalizeApiMessages(messages: Array<any>, conversationId: string): Array<ChatMessage> {
  return messages.map((message: any) => {
    const base = {
      ...message,
      conversation_id: conversationId,
      given_feedback: message.feedback?.rating === 1 ? 'positive' : message.feedback?.rating === -1 ? 'negative' : null,
      mentions: [],
      attached_context: message.role === 'user' && message.attachedContext ? message.attachedContext : [],
    }

    // Extract thinking content from historical assistant messages
    if (message.role === 'assistant') {
      // Use the dedicated reasoning field from the API if available
      if (typeof message.reasoning === 'string' && message.reasoning.trim()) {
        base.thinkingContent = message.reasoning
        base.isThinkingStreaming = false
      } else if (typeof message.content === 'string') {
        // Fallback: extract from XML tags in content (e.g. deepseek-r1)
        const { thinking, response } = extractThinkingContent(message.content, message.model_id)
        if (thinking) {
          base.content = response
          base.thinkingContent = thinking
          base.isThinkingStreaming = false
        }
      }
    }

    // Fix for toolCalls property to match ToolCall[] type if present
    if (message.role === 'assistant' && Array.isArray(message.toolCalls)) {
      const toolResponseMap = new Map<string, any>()
      messages.forEach((msg: any) => {
        if (msg.role === 'tool' && msg.toolCallId) {
          toolResponseMap.set(msg.toolCallId, msg)
        }
      })

      base.toolCalls = message.toolCalls.map((toolCall: any) => {
        const toolResponse = toolResponseMap.get(toolCall.id)
        const toolName = toolCall.function?.name ?? toolResponse?.name ?? ''

        const isToolFailure =
          toolResponse &&
          typeof toolResponse.content === 'string' &&
          (toolResponse.content.includes('not found') || toolResponse.content.includes('Failed to call tool:'))

        let parsedArguments: Record<string, unknown> = {}
        try {
          const args = toolCall.function?.arguments
          parsedArguments = typeof args === 'string' ? JSON.parse(args) : (args ?? {})
        } catch {
          parsedArguments = {}
        }

        const reconstructed: Record<string, unknown> = {
          id: toolCall.id,
          toolName,
          toolInput: parsedArguments,
          toolType: isToolFailure ? 'failure' : 'builtin',
          status: isToolFailure ? 'failed' : toolResponse ? 'success' : 'ready',
          result: toolResponse?.content,
          error: isToolFailure ? toolResponse.content : undefined,
          metadata: toolResponse?.metadata,
          steps: toolResponse?.steps,
          view: toolCall.view,
        }

        if (toolName === 'subagent' && toolResponse?.content) {
          try {
            const parsed =
              typeof toolResponse.content === 'string' ? JSON.parse(toolResponse.content) : toolResponse.content
            if (Array.isArray(parsed)) {
              reconstructed.subagents = parsed.map((result: unknown, idx: number) => ({
                index: idx,
                messageId: '',
                parentToolCallId: toolCall.id,
                content: typeof result === 'string' ? result : '',
                thinkingContent: '',
                isThinkingStreaming: false,
                status: 'completed',
              }))
            }
          } catch {
            // Content isn't parseable — leave subagents empty
          }
        }

        return reconstructed
      })

      // Reconstruct contentParts so the renderer treats reloaded messages
      // the same as streamed ones — tools fold into the collapsible and
      // the response text trails out via FinalMessageReveal.
      const contentParts = reconstructContentParts(
        base.toolCalls as Array<ToolCall>,
        typeof base.content === 'string' ? base.content : '',
      )
      if (contentParts) base.contentParts = contentParts
    }

    return base
  })
}

/**
 * Standalone fetcher for chat messages. Returns the raw API messages array
 * (un-normalized) so callers can decide whether/how to merge with the atom.
 * Returns [] on error. Used by useLoadMessages and by prefetch paths.
 */
export async function fetchChatMessages(chatId: string, getAuthToken: () => Promise<string>): Promise<Array<any>> {
  const response = await v1Client.GET('/conversations/{id}/messages', {
    params: { path: { id: chatId } },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getAuthToken()}`,
    },
  })
  if (response.error) {
    console.error(`Failed to load chat: ${chatId}`, response.error)
    return []
  }
  return response.data?.data ?? []
}

export function useLoadMessages(callback?: () => void) {
  const { setMessages } = useMessagesStore()
  const activeChatId = useActiveChatId()
  const { getAuthToken } = useAuth()

  // Read the atom when the fetch resolves rather than capturing it at render
  // time, so a concurrent atom update during the fetch can't make the merge
  // heuristic decide on a stale length. A read, not a subscription — this hook
  // would otherwise re-render its host on every streaming token.
  const readAllMessages = useReadAllChatMessages()

  return useQuery({
    queryKey: messagesQueryKey(activeChatId),
    queryFn: async () => {
      if (!activeChatId) return { messages: [] }

      const messages = await fetchChatMessages(activeChatId, getAuthToken)

      if (!activeChatId || !messages.length) return { messages: [] }

      const normalized = normalizeApiMessages(messages, activeChatId)

      const existing = readAllMessages()[activeChatId] ?? []

      // Carry client-minted identity forward across refetches: group keys
      // prefer clientId, so dropping it here would swap every touched
      // bubble's key (`user-${clientId}` → `user-${backendId}`) and
      // flash-remount them on reopen.
      const clientIdsById = new Map<string, string>()
      for (const m of existing) {
        if (m.clientId !== undefined && m.id !== undefined) {
          clientIdsById.set(m.id, m.clientId)
        }
      }
      const fetched =
        clientIdsById.size === 0
          ? normalized
          : normalized.map((m) => {
              const clientId = clientIdsById.get(m.id)
              return clientId ? { ...m, clientId } : m
            })

      // Per-message merge (HIG-5971): a backend-ahead refetch replaces the
      // atom, but in-flight streamed content (a longer local assistant fold,
      // the unadopted placeholder, an unpersisted streaming assistant) always
      // survives — the old wholesale replace reverted streamed text and
      // swapped message identity on reopen.
      setMessages(activeChatId, mergeRefetchedMessages(fetched, existing, isTurnLive(activeChatId)))

      callback?.()

      return { messages }
    },
    enabled: !!activeChatId,
    ...messagesFreshnessOptions,
    gcTime: 60 * 60 * 1000,
    // Not on focus or reconnect either — a reconnect mid-edit/stream would
    // clobber the in-flight user message + assistant placeholder before the
    // backend has persisted them.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

const STOP_TURN_TIMEOUT_MS = 10_000

export function useEditMessage() {
  // A read, not a subscription: the transcript is only consulted when the user
  // actually submits an edit, so subscribing would re-render every consumer of
  // this hook (and churn the returned callback's identity) per streaming token.
  const readAllMessages = useReadAllChatMessages()
  const { setMessages } = useMessagesStore()
  const submitMessage = useSubmitMessage()
  const stopMessage = useStopMessage()
  const { getAuthToken } = useAuth()
  const addChatToast = useAddChatToast()

  return useCallback(
    async (conversationId: string, messageId: string, content: string) => {
      // Validate input
      if (!content.trim()) {
        console.error('Cannot edit message with empty content')
        addChatToast({
          title: 'Cannot edit an empty message',
          message: 'Please enter a message and try again.',
          type: 'error',
        })
        return
      }

      // Queued follow-ups would drain against the transcript this edit
      // truncates — and stopping below would start the next one mid-edit.
      if (getQueueForChat(conversationId).length > 0) {
        addChatToast({
          title: 'Could not edit this message',
          message: 'Send or cancel the queued messages first.',
          type: 'error',
        })
        return
      }

      // Stop a still-generating reply first. Otherwise the edited message
      // submits while the old turn streams and lands in the queue panel, and
      // the old reply's id isn't known yet so it survives the delete below.
      if (isTurnLive(conversationId) && (await stopMessage(conversationId))) {
        const outcome = await waitForTurnEnd(conversationId, STOP_TURN_TIMEOUT_MS)
        if (outcome === 'timed-out') console.warn(`Stopped turn did not end before editing: ${conversationId}`)
      }

      // Filter out the messages with undefined id
      const currentMessages = (readAllMessages()[conversationId] ?? []).filter(
        (message: ChatMessage) => message.id !== undefined,
      )

      const messageIndex = currentMessages.findIndex((message: ChatMessage) => message.id === messageId)
      if (messageIndex === -1) {
        console.error('Message not found for editing:', messageId)
        return
      }
      const message = currentMessages[messageIndex]

      // Get the messages to delete (the edited message and all messages after it)
      const messagesToDelete = currentMessages.slice(messageIndex)
      const messageIdsToDelete = messagesToDelete.map((message: ChatMessage) => message.id)

      const previousAttachedContext = message.role === 'user' ? (message.attached_context ?? []) : []

      // Check if user is trying to delete the entire message history i.e. the first message
      if (messageIdsToDelete.length === currentMessages.length) {
        // Just create a new chat and conversation with the new content
        await submitMessage(content, {
          isNewChat: true,
          // Use pre-finalized context directly to avoid re-upload/finalization
          attachedContext: previousAttachedContext,
        })
        return
      }

      // Delete messages from backend if there are any
      if (messageIdsToDelete.length > 0) {
        try {
          const response = await v1Client.DELETE('/conversations/{id}/messages', {
            body: {
              ids: messageIdsToDelete,
            },
            params: {
              path: {
                id: conversationId,
              },
            },
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAuthToken()}`,
            },
          })

          if (response.error) {
            console.error(`Failed to delete messages: ${conversationId}`, response.error)
            // Don't proceed with edit if deletion failed
            addChatToast({
              title: 'Failed to edit message',
              message: 'Please try again later or start a new chat.',
              type: 'error',
            })
            return
          }
        } catch (error) {
          console.error('Error deleting messages:', error)
          // Don't proceed with edit if deletion failed
          addChatToast({
            title: 'Failed to edit message',
            message: 'Please try again later or start a new chat.',
            type: 'error',
          })
          return
        }
      }

      // Update the message content and remove subsequent messages
      const updatedMessage = { ...message, content }
      const messagesBeforeEdit = currentMessages.slice(0, messageIndex)

      setMessages(conversationId, [...messagesBeforeEdit])

      // Submit the updated message
      try {
        await submitMessage(updatedMessage.content, {
          conversationId,
          // Use pre-finalized context directly to avoid re-upload/finalization
          attachedContext: previousAttachedContext,
        })
      } catch (error) {
        console.error('Error submitting edited message:', error)
        // Optionally revert the local state change if submission fails
        setMessages(conversationId, currentMessages)
        addChatToast({
          title: 'Failed to edit message',
          message: 'Please try again later or start a new chat.',
          type: 'error',
        })
      }
    },
    [readAllMessages, setMessages, submitMessage, stopMessage, getAuthToken, addChatToast],
  )
}
